import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Download, Check } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import DialogBox from './core-components/DialogBox/DialogBox';
import { type AddStudentData } from '../hooks/useAddStudent';

interface StudentFormPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  onCancel: () => void;
  formData: AddStudentData;
  isSubmitting?: boolean;
}

const StudentFormPreviewDialog: React.FC<StudentFormPreviewDialogProps> = ({
  open,
  onClose,
  onSave,
  onCancel,
  formData,
  isSubmitting = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(undefined);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    // Handle photo URL
    if (formData.photo) {
      if (formData.photo instanceof File) {
        const url = URL.createObjectURL(formData.photo);
        setPhotoUrl(url);
        return () => URL.revokeObjectURL(url);
      } else if (typeof formData.photo === 'string') {
        setPhotoUrl(formData.photo);
      }
    }
  }, [formData.photo]);

  const handleDownload = async () => {
    if (!formRef.current) return;

    setIsDownloading(true);
    try {
      // 1. Wait for images to load
      const images = formRef.current.querySelectorAll("img");
      await Promise.all(
        Array.from(images).map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete && img.naturalWidth > 0) {
                resolve(true);
              } else {
                img.onload = () => resolve(true);
                img.onerror = () => resolve(true);
              }
            })
        )
      );

      // Additional wait to ensure everything is rendered
      await new Promise((r) => setTimeout(r, 500));

      // Your template is EXACT A4 size (595.5 × 842.25)
      const a4WidthPx = 595.5;
      const a4HeightPx = 842.25;

      // 2. Capture at MAXIMUM resolution (6x scale for ultra-sharp text and images)
      // Maximum scale for highest possible quality - captures at 6x native resolution
      // The element is already exactly A4 size, so we capture it naturally and scale up for quality
      const scale = 6;
      const canvas = await html2canvas(formRef.current, {
        scale,
        backgroundColor: "#ffffff",
        useCORS: true,
        allowTaint: true,
        logging: false,
        removeContainer: false,
        imageTimeout: 20000, // Increased timeout for high-res rendering
        // Don't set width/height - let it capture at natural element size
        onclone: (clonedDoc) => {
          // Ensure all fonts are loaded and ready
          const clonedWindow = clonedDoc.defaultView;
          if (clonedWindow) {
            clonedWindow.document.fonts.ready;
          }
          // Optimize all images for maximum quality rendering
          const clonedImages = clonedDoc.querySelectorAll('img');
          clonedImages.forEach((img) => {
            if (img.src.endsWith('.svg')) {
              // Force high resolution rendering for SVG
              (img as HTMLImageElement).style.imageRendering = 'auto';
            }
            // Ensure all images are rendered at maximum quality with crisp edges
            (img as HTMLImageElement).style.imageRendering = 'crisp-edges';
            // Remove any size constraints to allow full resolution
            (img as HTMLImageElement).style.maxWidth = 'none';
            (img as HTMLImageElement).style.maxHeight = 'none';
          });
        },
      });

      // Use maximum quality JPEG (0.99) for near-lossless quality
      // 0.99 is the maximum JPEG quality - provides lossless-like quality
      // Combined with 6x scale, this gives the absolute maximum PDF quality
      const imgData = canvas.toDataURL("image/jpeg", 0.99);

      // 3. Create A4 PDF in EXACT size (original template dimensions)
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: [a4WidthPx, a4HeightPx], // EXACT A4 SIZE - matches template
        compress: true, // Enable compression
      });

      // 4. Draw image EXACTLY in A4 dimensions — no stretch, no shift
      // Canvas is scaled (6x), but we scale it back to original A4 size in PDF
      // Using "SLOW" compression for MAXIMUM quality - best possible quality preservation
      pdf.addImage(
        imgData,
        "JPEG",
        0,
        0,
        a4WidthPx,
        a4HeightPx,
        undefined,
        "SLOW" // Maximum quality compression - preserves all detail from 6x scale capture
      );

      // 5. Save file
      const fileName = `Student_Registration_${formData.candidateName
        .replace(/\s+/g, "_")
        .trim()}_${new Date().toISOString().slice(0, 10)}.pdf`;

      pdf.save(fileName);
    } catch (err) {
      console.error("PDF Error:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <DialogBox
      open={open}
      onClose={onClose}
      title="Student Registration Preview"
      width="95%"
      maxHeight="90vh"
      hideScrollbar={true}
      showCloseIcon={true}
      actions={[
        {
          text: 'Cancel',
          variant: 'outlined',
          color: 'secondary',
          handler: onCancel,
          disabled: isSubmitting,
        },
        {
          text: isSubmitting ? 'Saving...' : 'Save Student',
          variant: 'contained',
          color: 'primary',
          handler: onSave,
          disabled: isSubmitting,
        },
      ]}
    >
      <Box
        ref={containerRef}
        sx={{
        position: 'relative', 
          width: '100%',
        maxHeight: 'calc(90vh - 120px)',
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          p: 2,
        }}
      >
        {/* Download Button */}
        <Tooltip title={isDownloading ? 'Generating PDF...' : 'Download PDF'}>
          <span style={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
            <IconButton
              onClick={handleDownload}
              disabled={isDownloading}
              sx={{
                backgroundColor: '#10b981',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#059669',
                },
                '&:disabled': {
                  backgroundColor: '#9ca3af',
                },
              }}
            >
              <Download />
            </IconButton>
          </span>
        </Tooltip>

        {/* Form Template Container */}
        <Box
          ref={formRef}
          sx={{
            position: 'relative',
            width: '595.5px',
            height: '842.25px',
            backgroundColor: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }}
        >
          {/* SVG Template Background */}
          <Box
            component="img"
            src="/studentFormTemplate.svg"
            alt="Student Form Template"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              zIndex: 1,
            }}
          />

          {/* Overlay Content - Form Data */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              width: '100%',
              height: '100%',
            }}
          >
            {/* Photo Box - Top Right */}
            <Box
              sx={{
                position: 'absolute',
                top: '52px',
                right: '41px',
                width: '105px',
                height: '110px',
                border: '2px solid #000',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {photoUrl ? (
                <Box
                  component="img"
                  src={photoUrl}
                  alt="Student Photo"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'fill',
                  }}
                />
              ) : null}
            </Box>

            {/* Center Name - Above Personal Information */}
            <Typography
              sx={{
                position: 'absolute',
                top: '143px',
                left: '133px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {(formData as any).centerName || (formData.center as any)?.name || ''}
            </Typography>

            {/* Personal Information - Values Only */}
            <Typography
              sx={{
                position: 'absolute',
                top: '216px',
                left: '140px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.candidateName || ''}
            </Typography>

            <Typography
              sx={{
                position: 'absolute',
                top: '216px',
                left: '416px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.fatherName || ''}
            </Typography>

            {/* Date of Birth - Left Column, Row 2 */}
            <Typography
              sx={{
                position: 'absolute',
                top: '244px',
                left: '140px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.dateOfBirth || ''}
            </Typography>

            {/* Mother Name - Right Column, Row 2 */}
            <Typography
              sx={{
                position: 'absolute',
                top: '244px',
                left: '416px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.motherName || ''}
            </Typography>

            {/* Email - Left Column, Row 3 */}
            <Typography
              sx={{
                position: 'absolute',
                top: '271px',
                left: '140px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.emailAddress || ''}
            </Typography>

            {/* Phone - Right Column, Row 3 */}
            <Typography
              sx={{
                position: 'absolute',
                top: '274px',
                left: '416px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.contactNumber || ''}
            </Typography>

            {/* Current Address - Left Column, Row 4 (full width) */}
            <Typography
              sx={{
                position: 'absolute',
                top: '298px',
                left: '140px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                maxWidth: '400px',
                wordWrap: 'break-word',
                lineHeight: 1.2,
              }}
            >
              {formData.currentAddress || ''}
            </Typography>

            {/* Aadhar Card No - Left Column, Row 5 */}
            <Typography
              sx={{
                position: 'absolute',
                top: '328px',
                left: '140px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.adharCardNo || ''}
            </Typography>

            {/* Gender - Right Column, Row 5 */}
            <Typography
              sx={{
                position: 'absolute',
                top: '328px',
                left: '413px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.gender || ''}
            </Typography>

            {/* State Name - Left Column, Row 6 */}
            <Typography
              sx={{
                position: 'absolute',
                top: '356px',
                left: '140px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.state || ''}
            </Typography>

            {/* City - Right Column, Row 6 */}
            <Typography
              sx={{
                position: 'absolute',
                top: '352px',
                left: '410px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
                textAlign: 'left',
                width: '180px',
                display: 'block',
              }}
            >
              {formData.city || ''}
            </Typography>

            {/* Country - Left Column, Row 7 */}
            <Typography
              sx={{
                position: 'absolute',
                top: '381px',
                left: '140px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.country || ''}
            </Typography>

            {/* Pincode - Right Column, Row 7 */}
            <Typography
              sx={{
                position: 'absolute',
                top: '378px',
                left: '409px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.pincode || ''}
            </Typography>

            {/* Employment Information - Values Only */}
            {/* Employer Name - Left Column */}
            <Typography
              sx={{
                position: 'absolute',
                top: '457px',
                left: '147px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.employerName || ''}
            </Typography>

            {/* Designation - Right Column */}
            <Typography
              sx={{
                position: 'absolute',
                top: '457px',
                left: '410px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.designation || ''}
            </Typography>

            {/* Academic Information - Values Only */}
            {/* Grade - Left Column */}
            <Typography
              sx={{
                position: 'absolute',
                top: '552px',
                left: '105px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.grade || ''}
            </Typography>

            {/* Course Type - Right Column */}
            <Typography
              sx={{
                position: 'absolute',
                top: '549px',
                left: '308px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {typeof formData.courseType === 'object' && formData.courseType !== null && 'name' in formData.courseType
                ? (formData.courseType as { name: string }).name
                : typeof formData.courseType === 'string'
                ? formData.courseType
                : ''}
            </Typography>

            {/* Courses - Left Column, full width */}
            <Typography
              sx={{
                position: 'absolute',
                top: '589px',
                left: '105px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                maxWidth: '400px',
                wordWrap: 'break-word',
                lineHeight: 1.2,
              }}
            >
              {typeof formData.course === 'object' && formData.course !== null && 'name' in formData.course
                ? (formData.course as { name: string }).name
                : typeof formData.course === 'string'
                ? formData.course
                : ''}
            </Typography>

            {/* Year - Left Column */}
            <Typography
              sx={{
                position: 'absolute',
                top: '620px',
                left: '105px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.year || ''}
            </Typography>

            {/* Month Session - Right Column */}
            <Typography
              sx={{
                position: 'absolute',
                top: '620px',
                left: '295px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.monthSession || ''}
            </Typography>

            {/* Session - Left Column */}
            <Typography
              sx={{
                position: 'absolute',
                top: '620px',
                left: '440px',
                fontSize: '0.55rem',
                color: '#000',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {formData.session || ''}
          </Typography>

            {/* Declaration Checkbox - Tick Mark Only */}
            <Box
              sx={{
                position: 'absolute',
                top: '710px',
                left: '54px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Check
                sx={{
                  fontSize: '1.5rem',
                  color: '#000',
                  fontWeight: 'bold',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </DialogBox>
  );
};

export default StudentFormPreviewDialog;
